import { memo, useCallback, useState } from "react";
import { useTranslate } from "helpers";
import { Modal } from "ui/components/Modal";
import JomImportIntro from "./components/JomImportIntro/JomImportIntro";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { JOMDropdown } from "ui/components/Dropdown/JOMDropdown";
import { DropdownOption } from "ui/components/Dropdown/DropdownAutocomplete/types";
import { Dialog } from "ui/components/Dialog";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import { Tooltip } from "ui/components/Tooltip";
import { Loader } from "ui/components/Loader";
import { CvhVehicleSearchRow } from "services/api/catalog/cvh/types";
import { importFleetVehiclesFromJomAPI } from "services/api/fleet/vehicles/api";
import { JomImportRequestItem } from "services/api/fleet/vehicles/types";
import {
  VehicleJomImportModalProps,
  JomSearchItem,
  JomImportFormValues,
} from "./types";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { JOM_IMPORT_FORM_SCHEMA } from "./validation";
import { showNotification } from "services/api/snackbar";

const initialFormValues: JomImportFormValues = {
  jomNumber: "",
  chassisNumber: "",
  registrationNumber: "",
  kilometers: "",
};

// Godina proizvodnje "od - do".
const formatVehicleYear = (row: CvhVehicleSearchRow): string => {
  if (row.yearFrom && row.yearTo) return `${row.yearFrom} - ${row.yearTo}`;
  if (row.yearFrom) return `${row.yearFrom} -`;
  return "";
};

// Sekundarni detalji: snaga · gorivo · oprema.
const formatVehicleDetails = (row: CvhVehicleSearchRow): string => {
  const power = row.power ? `${row.power} kW` : null;
  return [power, row.fuelType, row.equipment].filter(Boolean).join(" · ");
};

const VehicleTooltipRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between text-sm mb-1 gap-4">
    <span className="text-slate-300">{label}</span>
    <span className="font-medium text-right max-w-[180px] truncate">{value}</span>
  </div>
);

// Ćelija vozila u stilu reda na pretrazi vozila: slika (s tooltipom) + marka/model/tip + godine/detalji.
const VehicleCell: React.FC<{ item: JomSearchItem }> = ({ item }) => {
  const row = item.vehicle;
  const name = row?.name || item.jomNumber || "-";
  const year = row ? formatVehicleYear(row) : "";
  const details = row ? formatVehicleDetails(row) : "";
  const subtitle = [year, details].filter(Boolean).join(" · ");

  const icon = (
    <div className="cursor-help">
      <TableIcon
        imageUrl={row?.image}
        initials={name.charAt(0).toUpperCase()}
        imageFit="cover"
        size="medium"
      />
    </div>
  );

  return (
    <div className="flex items-center gap-3">
      {row?.image ? (
        <Tooltip position="top-right" size="medium" trigger={icon}>
          <div className="min-w-[240px]">
            <div className="mb-3 rounded overflow-hidden">
              <img src={row.image} alt={name} className="w-full h-[120px] object-cover rounded" />
            </div>
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
            <VehicleTooltipRow label="Naziv" value={name} />
            <VehicleTooltipRow label="JOM" value={item.jomNumber} />
            {year && <VehicleTooltipRow label="Godina" value={year} />}
            {row.power != null && <VehicleTooltipRow label="Snaga" value={`${row.power} kW`} />}
            {row.fuelType && <VehicleTooltipRow label="Gorivo" value={row.fuelType} />}
            {row.equipment && <VehicleTooltipRow label="Oprema" value={row.equipment} />}
          </div>
        </Tooltip>
      ) : (
        icon
      )}
      <div className="min-w-0">
        <div className="text-md font-medium text-slate-800 truncate">{name}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
      </div>
    </div>
  );
};

const VehicleJomImportModal: React.FC<VehicleJomImportModalProps> = (props) => {
  const { show, onClose, onImportSuccess } = props;

  const { t } = useTranslate();
  const [formValues, setFormValues] =
    useState<JomImportFormValues>(initialFormValues);
  const [searchItems, setSearchItems] = useState<JomSearchItem[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] =
    useState<CvhVehicleSearchRow | null>(null);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors({});
    },
    []
  );

  const handleJomChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      setFormValues((prev) => ({
        ...prev,
        jomNumber: item.value,
      }));
      setErrors({});
    },
    []
  );

  const handleJomSelect = useCallback((row: CvhVehicleSearchRow | null) => {
    setSelectedVehicle(row);
  }, []);

  const handleAddItem = useCallback(() => {
    const isValid = validateYup(formValues, JOM_IMPORT_FORM_SCHEMA, setErrors);
    if (!isValid) {
      return;
    }

    const { jomNumber, chassisNumber, registrationNumber, kilometers } = formValues;

    const newItem: JomSearchItem = {
      id: `${Date.now()}`,
      jomNumber,
      chassisNumber,
      registrationNumber,
      kilometers,
      vehicle: selectedVehicle,
      error: null,
    };

    setSearchItems((prev) => [...prev, newItem]);
    setFormValues(initialFormValues);
    setSelectedVehicle(null);
  }, [formValues, selectedVehicle]);

  const handleRemoveClick = useCallback((id: string) => {
    setItemToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      setSearchItems((prev) => prev.filter((item) => item.id !== itemToDelete));
    }
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  }, [itemToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  }, []);

  const handleClose = useCallback(() => {
    setFormValues(initialFormValues);
    setSearchItems([]);
    setSelectedVehicle(null);
    setErrors({});
    onClose();
  }, [onClose]);

  const handleImport = useCallback(async () => {
    if (searchItems.length === 0) {
      showNotification("Morate dodati barem jedno vozilo za uvoz.", {
        variant: "error",
      });
      return;
    }

    setIsImporting(true);
    try {
      const items: JomImportRequestItem[] = searchItems.map((item) => ({
        jom: item.jomNumber,
        vinNumber: item.chassisNumber || null,
        registrationPlate: item.registrationNumber || null,
        kmTraveled: item.kilometers ? Number(item.kilometers) : null,
      }));

      const result = await importFleetVehiclesFromJomAPI(items);

      if (result.errors.length === 0) {
        showNotification(`Uspješno uvezeno ${result.successCount} vozila.`, {
          variant: "success",
        });
        onImportSuccess();
        handleClose();
        return;
      }

      // Djelomičan uspjeh — zadrži samo neuspješne stavke s porukom greške.
      const failedRows = new Set(result.errors.map((e) => e.row));
      setSearchItems((prev) =>
        prev
          .map((item, index) => {
            const rowError = result.errors.find((e) => e.row === index + 1);
            return rowError ? { ...item, error: rowError.message } : item;
          })
          .filter((_, index) => failedRows.has(index + 1))
      );

      showNotification(
        `Uvezeno ${result.successCount} vozila, ${result.errors.length} neuspješno.`,
        { variant: result.successCount > 0 ? "warning" : "error" }
      );

      if (result.successCount > 0) {
        onImportSuccess();
      }
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsImporting(false);
    }
  }, [searchItems, onImportSuccess, handleClose]);

  const successfulItemsCount = searchItems.length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="full"
      position="side"
      intro={{
        content: (
          <JomImportIntro items={searchItems} selectedVehicle={selectedVehicle} />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={t("admin.fleet.vehicle.jom.import.title")}
        onHide={handleClose}
      />
      <ModalBody>
        <div className="space-y-4 p-8 mt-12">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              {t("admin.fleet.vehicle.jom.import.title")}
            </h1>
            <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
              {t("admin.fleet.vehicle.jom.import.description")}
            </p>
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-[2]">
              <JOMDropdown
                name="jomNumber"
                label="admin.fleet.vehicle.jom.import.jomnumber"
                value={formValues.jomNumber}
                onChange={handleJomChange}
                onSelect={handleJomSelect}
                placeholder="admin.fleet.vehicle.jom.import.jomnumber.placeholder"
                error={errors.jomNumber}
                width="full"
                required
              />
            </div>
            <div className="flex-1">
              <TextField
                type="text"
                name="chassisNumber"
                label={t("admin.fleet.vehicle.jom.import.chassisnumber")}
                value={formValues.chassisNumber}
                onChange={handleInputChange}
                placeholder={t("admin.fleet.vehicle.jom.import.chassisnumber.placeholder")}
              />
            </div>
            <div className="flex-1">
              <TextField
                type="text"
                name="registrationNumber"
                label={t("admin.fleet.vehicle.jom.import.registrationnumber")}
                value={formValues.registrationNumber}
                onChange={handleInputChange}
                placeholder={t("admin.fleet.vehicle.jom.import.registrationnumber.placeholder")}
              />
            </div>
            <div className="flex-1">
              <TextField
                type="number"
                name="kilometers"
                label={t("admin.fleet.vehicle.residualvalue.mileagekm")}
                value={formValues.kilometers}
                onChange={handleInputChange}
                placeholder={t("admin.fleet.vehicle.residualvalue.mileagekm.placeholder")}
                groupAppend="km"
                error={errors.kilometers}
                required
              />
            </div>
            <div className="">
              <Button
                variant="primary"
                onClick={handleAddItem}
              >
                +
              </Button>
            </div>
          </div>

          {searchItems.length > 0 && (
            <div className="border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      {t("admin.fleet.vehicle.jom.import.result")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      {t("admin.fleet.vehicle.residualvalue.mileagekm")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      {t("admin.fleet.vehicle.jom.import.jomnumber")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      {t("admin.fleet.vehicle.jom.import.chassisnumber")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      {t("admin.fleet.vehicle.jom.import.registrationnumber")}
                    </th>

                    <th className="px-4 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searchItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <VehicleCell item={item} />
                        {item.error && (
                          <div className="text-xs text-red-600 mt-1">
                            {item.error}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {item.jomNumber || "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {item.kilometers
                          ? `${Number(item.kilometers).toLocaleString("hr-HR")} km`
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {item.chassisNumber || "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {item.registrationNumber || "-"}
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleRemoveClick(item.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ModalBody>
      <Loader
        isLoading={isImporting}
        overlay
        message="Uvoz vozila u tijeku..."
        className="fixed inset-y-0 right-0 w-1/2"
      />
      <div className="px-4 py-3 fixed bottom-0 right-0 w-1/2 bg-slate-50 border-t border-slate-200 flex justify-between gap-3">
        <Button variant="outlined" onClick={handleClose}>
          {t("admin.close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          loading={isImporting}
        >
          {t("admin.fleet.vehicle.jom.import.button")}
          {successfulItemsCount > 0 && ` (${successfulItemsCount})`}
        </Button>
      </div>
      <Dialog
        open={openDeleteDialog}
        title={t("admin.fleet.vehicle.jom.import.dialog.delete.title")}
        message={t("admin.fleet.vehicle.jom.import.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.jom.import.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.jom.import.dialog.delete.canceltext")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Modal>
  );
};

export default memo(VehicleJomImportModal);
